import { Bluelagoon, useBluelagoon } from "@bluelagoon";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Icon from "./Icon";
import InternalLink from "./InternalLink";
import Logo from "@ui/Logo";
import { useAuth } from "../context/AuthContext";
import { User, Sun, Zap, Coins } from "lucide-react";
import IntegrationAlertBadge from "../Integrations/IntegrationAlertBadge";
import { useCloseOnClickOutside } from "../hooks/useCloseMenu";
import WorkspaceSwitcher from "./WorkspaceSwitcher";
import { toast } from "sonner";

import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@shadcn-in-nx/ui/tooltip";


function UserSection({ isOpen, user, hideProfile = false }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const {
        logout,
        interfaceStyle,
        setShowAuthModal,
        isAdmin,
        isSuperAdmin,
        isClient,
        handleGetCompanyAgentUICredit: handleGetCompanyAgentUICreditFromParent,
    } = useAuth();

    const toggleMenu = () => setIsMenuOpen((prev) => !prev);
    const closeMenu = () => setIsMenuOpen(false);
    const [companyAgentUICredit, setCompanyAgentUICredit] = useState(() => {
        return window.localStorage.getItem("companyAgentUICredit") || 0;
    });
    const menuRef = useCloseOnClickOutside(closeMenu);
    const didIntiailFetch = useRef(false);
    const lastCreditsFetchAmount = useRef(null);

    const { call: getCompanyAgentUICredit } = useBluelagoon({
        method: "getCompanyAgentUICredit",
        setIsLoading: false,
    });

    const handleGetCompanyAgentUICredit = React.useCallback(() => {
        if (isClient) return;
        getCompanyAgentUICredit().then((response) => {
            setCompanyAgentUICredit(response?.data?.credits);
            lastCreditsFetchAmount.current = response?.data?.credits;
            window.localStorage.setItem(
                "companyAgentUICredit",
                response?.data?.credits
            );
            if (
                lastCreditsFetchAmount.current !== companyAgentUICredit &&
                companyAgentUICredit <= 0
            ) {
                handleGetCompanyAgentUICreditFromParent();
            }
        });
    }, [
        getCompanyAgentUICredit,
        companyAgentUICredit,
        handleGetCompanyAgentUICreditFromParent,
    ]);

    React.useEffect(() => {
        if (!user?.id) return;

        if (!didIntiailFetch.current) {
            handleGetCompanyAgentUICredit();
            didIntiailFetch.current = true;
        }

        // Refresh every 2 minutes
        const intervalId = setInterval(() => {
            handleGetCompanyAgentUICredit();
        }, 2 * 60 * 1000);

        return () => clearInterval(intervalId);
    }, [user?.id]);

    if (!user) {
        return (
            <div className="mt-auto pt-4 border-t border-gray-200 ">
                {isOpen ? (
                    <button
                        onClick={() => setShowAuthModal(true)}
                        className="w-full flex items-center justify-center p-2 text-gray-900 rounded-lg  hover:bg-gray-100  border border-gray-200 "
                    >
                        <User className="w-5 h-5 mr-2" />
                        <span className="text-sm font-medium">
                            {t("sidebar.auth.signIn")}
                        </span>
                    </button>
                ) : (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                onClick={() => setShowAuthModal(true)}
                                className="p-2 text-gray-900 rounded-lg  hover:bg-gray-100  border border-gray-200 "
                            >
                                <User className="w-5 h-5" />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                            {t("sidebar.auth.signIn")}
                        </TooltipContent>
                    </Tooltip>
                )}
            </div>
        );
    }

    return (
        <div className="mt-auto pt-4 border-t border-gray-200 ">
            {isOpen &&
                !isClient &&
                companyAgentUICredit !== undefined &&
                companyAgentUICredit > -1 && (
                    <div className="px-2 mb-2 flex flex-col gap-2">
                        <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center text-gray-500">
                                <Coins className="w-4 h-4 mr-2" />
                                <span>{t("sidebar.credits.label")}</span>
                            </div>
                            <span
                                className={`font-semibold ${
                                    companyAgentUICredit <= 0
                                        ? "text-red-500"
                                        : "text-gray-900"
                                }`}
                            >
                                {Math.round(companyAgentUICredit)}
                            </span>
                        </div>
                        {(isAdmin || isSuperAdmin) &&
                            companyAgentUICredit <= 0 && (
                                <button
                                    onClick={() => {
                                        localStorage.setItem(
                                            "billing_return_url",
                                            location.pathname + location.search
                                        );
                                        navigate("/billing");
                                    }}
                                    className="w-full flex items-center justify-center px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                                >
                                    {t("sidebar.credits.addCredits")}
                                </button>
                            )}
                    </div>
                )}
            {isOpen ? (
                <div className="relative">
                    <button
                        type="button"
                        onClick={toggleMenu}
                        className="flex items-center w-full p-2 text-gray-900 rounded-lg  hover:bg-gray-100 "
                    >
                        <div className="min-w-[24px] flex items-center justify-center">
                            {user?.avatar ? (
                                <img
                                    src={user.avatar}
                                    alt="User avatar"
                                    className="w-6 h-6 rounded-full"
                                    referrerPolicy="no-referrer"
                                />
                            ) : (
                                <User className="w-5 h-5" />
                            )}
                        </div>
                        <div className="ml-3 flex-1 text-left">
                            <p className="text-sm font-medium truncate">
                                {user?.fullName}
                            </p>
                            <p className="text-xs text-gray-500  truncate">
                                {user?.email}
                            </p>
                        </div>
                        <Icon
                            name="ChevronUp"
                            size={16}
                            className={`transition-transform ${
                                isMenuOpen ? "rotate-180" : ""
                            }`}
                        />
                    </button>

                    {isMenuOpen && (
                        <>
                            <div
                                className="fixed inset-0 z-30"
                                onClick={closeMenu}
                            />
                            <div
                                className="absolute bottom-full left-0 w-full mb-1 bg-white  rounded-lg shadow-lg border border-gray-200  z-40"
                                ref={menuRef}
                            >
                                <ul className="py-1">
                                    {!hideProfile ? (
                                        <li>
                                            <InternalLink
                                                to="/profile"
                                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100   "
                                                onClick={closeMenu}
                                            >
                                                {t("sidebar.auth.profile")}
                                            </InternalLink>
                                        </li>
                                    ) : null}
                                    <li>
                                        <button
                                            onClick={() => {
                                                logout();
                                                closeMenu();
                                            }}
                                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100   "
                                        >
                                            {t("sidebar.auth.signOut")}
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </>
                    )}
                </div>
            ) : (
                <div className="flex justify-center">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                onClick={toggleMenu}
                                className="p-2 text-gray-900 rounded-lg  hover:bg-gray-100 "
                            >
                                {user?.avatar ? (
                                    <img
                                        src={user.avatar}
                                        alt="User avatar"
                                        className="w-6 h-6 rounded-full"
                                        referrerPolicy="no-referrer"
                                    />
                                ) : (
                                    <User className="w-5 h-5" />
                                )}
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                            {user?.fullName || "User"}
                        </TooltipContent>
                    </Tooltip>

                    {isMenuOpen && (
                        <>
                            <div
                                className="fixed inset-0 z-30"
                                onClick={closeMenu}
                            />
                            <div
                                className="absolute left-full bottom-0 ml-2 bg-white  rounded-lg shadow-lg border border-gray-200  z-40 min-w-[200px]"
                                ref={menuRef}
                            >
                                <div className="px-4 py-3 border-b border-gray-100 ">
                                    <p className="text-sm text-gray-900 ">
                                        {user?.fullName}
                                    </p>
                                    <p className="text-sm font-medium text-gray-900 truncate ">
                                        {user?.email}
                                    </p>
                                    <p className="text-xs text-gray-500  mt-1">
                                        {interfaceStyle === "expert"
                                            ? t("sidebar.interfaceMode.expert")
                                            : t("sidebar.interfaceMode.simple")}
                                    </p>
                                </div>
                                <ul className="py-1">
                                    {!hideProfile ? (
                                        <li>
                                            <InternalLink
                                                to="/profile"
                                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100   "
                                                onClick={closeMenu}
                                            >
                                                {t("sidebar.auth.profile")}
                                            </InternalLink>
                                        </li>
                                    ) : null}
                                    <li>
                                        <button
                                            onClick={() => {
                                                logout();
                                                closeMenu();
                                            }}
                                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100   "
                                        >
                                            {t("sidebar.auth.signOut")}
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function InterfaceStyleToggle({ isOpen, interfaceStyle, onToggle }) {
    const { t } = useTranslation();

    if (!isOpen) return null;

    return (
        <div className="mb-4 p-2">
            <button
                onClick={onToggle}
                className="w-full flex items-center justify-between p-2 text-gray-900 rounded-lg  hover:bg-gray-100  transition-colors border border-gray-200 "
                title={`Currently in ${
                    interfaceStyle === "expert"
                        ? t("sidebar.interfaceMode.expert")
                        : t("sidebar.interfaceMode.simple")
                } mode. Click to switch to ${
                    interfaceStyle === "expert"
                        ? t("sidebar.interfaceMode.simple")
                        : t("sidebar.interfaceMode.expert")
                } mode.`}
            >
                <div className="flex items-center">
                    {interfaceStyle === "expert" ? (
                        <Zap className="w-4 h-4 mr-2 text-blue-500" />
                    ) : (
                        <Sun className="w-4 h-4 mr-2 text-yellow-500" />
                    )}
                    <span className="text-sm font-medium">
                        {interfaceStyle === "expert"
                            ? t("sidebar.interfaceMode.expert")
                            : t("sidebar.interfaceMode.simple")}
                    </span>
                </div>
                <Icon name="ChevronRight" size={14} className="text-gray-400" />
            </button>
        </div>
    );
}

function CollapsedInterfaceStyleToggle({ interfaceStyle, onToggle }) {
    const { t } = useTranslation();

    return (
        <div className="mb-4 flex justify-center">
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        onClick={onToggle}
                        className="p-2 text-gray-900 rounded-lg  hover:bg-gray-100  transition-colors border border-gray-200 "
                    >
                        {interfaceStyle === "expert" ? (
                            <Zap className="w-5 h-5 text-blue-500" />
                        ) : (
                            <Sun className="w-5 h-5 text-yellow-500" />
                        )}
                    </button>
                </TooltipTrigger>
                <TooltipContent side="right">
                    {interfaceStyle === "expert"
                        ? t("sidebar.interfaceMode.expert")
                        : t("sidebar.interfaceMode.simple")}
                </TooltipContent>
            </Tooltip>
        </div>
    );
}

function Sidebar({ isOpen, setIsOpen, menu, user, hideProfile = false }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    // Initialize collapsed state based on menu items that are collapsable
    const [collapsedSections, setCollapsedSections] = useState(() => {
        const initialState = {};
        menu.forEach((item) => {
            if (item.section && item.collapsable) {
                initialState[item.label] = true; // true means collapsed
            }
        });
        return initialState;
    });

    const { interfaceStyle, toggleInterfaceStyle } = useAuth();

    // Helper function to check if a route is active
    const isActiveRoute = (route) => {
        if (!route) return false;
        return (
            location.pathname === route ||
            location.pathname.startsWith(`${route}/`)
        );
    };

    const toggleSection = (sectionLabel) => {
        setCollapsedSections((prev) => ({
            ...prev,
            [sectionLabel]: !prev[sectionLabel],
        }));
    };

    // Helper function to close sidebar on mobile
    const handleMenuItemClick = (e, route, itemOnClick) => {
        // Sentinel route "#" marks an action item: invoke onClick, suppress navigation.
        if (route === "#") {
            e.preventDefault();
            if (itemOnClick) itemOnClick(e);
            return;
        }
        // Set return URL when navigating to billing
        if (route === "/billing") {
            localStorage.setItem(
                "billing_return_url",
                location.pathname + location.search
            );
        }
        // Close sidebar on mobile (using matchMedia for better browser compatibility)
        const isMobile = window.matchMedia("(max-width: 1023px)").matches;
        if (isMobile && isOpen) {
            // Prevent default navigation
            e.preventDefault();
            // Close the sidebar
            setIsOpen();
            // Navigate after a short delay to ensure state is saved
            setTimeout(() => {
                navigate(route);
            }, 100);
            return;
        }
        // Call original onClick if it exists
        if (itemOnClick) {
            itemOnClick(e);
        }
    };

    const handleCompanySwitch = async (companyId) => {
        try {
            const response = await Bluelagoon.switchCompany({ companyId });

            // Get the user's role in the new company
            const userRole = response.data?.user?.roles?.[0]?.role?.name;

            // Redirect based on role
            if (userRole === "client") {
                window.location.href = "/client";
            } else if (
                userRole === "creator" ||
                userRole === "admin" ||
                userRole === "super-admin" ||
                userRole === "owner"
            ) {
                window.location.href = "/";
            } else {
                window.location.href = "/";
            }
        } catch (error) {
            console.error("Error switching company:", error);
            toast.error(t("sidebar.workspace.switchError", "Failed to switch workspace. Please try again."));
        }
    };

    // Group menu items by their sections
    const menuSections = menu.reduce((acc, item) => {
        if (item.section) {
            acc.push({
                section: item,
                items: [],
            });
        } else if (acc.length > 0) {
            acc[acc.length - 1].items.push(item);
        }
        return acc;
    }, []);

    return (
        <>
            {/* Mobile overlay with transition */}
            <div
                className={`fixed inset-0 bg-black/50 lg:hidden z-20 transition-opacity duration-300 ${
                    isOpen ? "opacity-100 visible" : "opacity-0 invisible"
                }`}
                onClick={() => setIsOpen(false)}
            />

            {/* Sidebar with transition */}
            <aside
                className={`fixed hide-for-print inset-y-0 left-0 z-30 bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ease-in-out ${
                    isOpen
                        ? "translate-x-0 w-64"
                        : "-translate-x-full lg:translate-x-0 lg:w-[68px]"
                }`}
                aria-label="Sidebar"
            >
                <div className="p-4 flex-1 flex flex-col overflow-hidden">
                    {/* Header with Logo and Toggle - visible on mobile */}
                    <div className="flex items-center justify-between mb-6 lg:hidden">
                        <Logo className="h-8" />
                        <button
                            onClick={() => setIsOpen(false)}
                            type="button"
                            className="grid place-content-center w-8 h-8 text-sm text-gray-500 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200   "
                        >
                            <span className="sr-only">
                                {t("sidebar.closeSidebar")}
                            </span>
                            <Icon name="X" size={20} />
                        </button>
                    </div>

                    {/* Workspace Switcher */}
                    <div className={isOpen ? "" : "hidden"}>
                        <WorkspaceSwitcher
                            user={user}
                            onSwitch={handleCompanySwitch}
                        />
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        <ul className="space-y-2 font-medium">
                            {menuSections.map(({ section, items }) => (
                                <React.Fragment key={section.label}>
                                    <li
                                        className={`flex items-center justify-between  font-normal uppercase text-xs opacity-50 ${
                                            !isOpen && "lg:hidden"
                                        }`}
                                    >
                                        <span>{section.label}</span>
                                        {section.collapsable && isOpen && (
                                            <button
                                                onClick={() =>
                                                    toggleSection(section.label)
                                                }
                                                className="p-1 hover:bg-gray-100  rounded"
                                            >
                                                <Icon
                                                    name={
                                                        collapsedSections[
                                                            section.label
                                                        ]
                                                            ? "ChevronRight"
                                                            : "ChevronDown"
                                                    }
                                                    size={14}
                                                />
                                            </button>
                                        )}
                                    </li>
                                    {(!section.collapsable ||
                                        !collapsedSections[section.label]) &&
                                        items.map((item) => (
                                            <li key={item.label}>
                                                {!isOpen ? (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <div
                                                                className={`flex items-center h-10 p-2 rounded-lg group lg:justify-center ${
                                                                    isActiveRoute(
                                                                        item.route
                                                                    )
                                                                        ? "bg-blue-50 text-blue-600"
                                                                        : "text-gray-900 hover:bg-gray-100"
                                                                }`}
                                                            >
                                                                <InternalLink
                                                                    to={
                                                                        item.route
                                                                    }
                                                                    onClick={(
                                                                        e
                                                                    ) =>
                                                                        handleMenuItemClick(
                                                                            e,
                                                                            item.route,
                                                                            item.onClick
                                                                        )
                                                                    }
                                                                    className="w-full h-full flex items-center justify-center"
                                                                >
                                                                    <div className="relative min-w-[18px] flex items-center justify-center">
                                                                        {item.icon && (
                                                                            <Icon
                                                                                name={
                                                                                    item.icon
                                                                                }
                                                                                size={
                                                                                    18
                                                                                }
                                                                                className={
                                                                                    isActiveRoute(
                                                                                        item.route
                                                                                    )
                                                                                        ? "text-blue-600"
                                                                                        : ""
                                                                                }
                                                                            />
                                                                        )}
                                                                        {item.alertBadge === "integrationAlerts" && (
                                                                            <IntegrationAlertBadge />
                                                                        )}
                                                                    </div>
                                                                </InternalLink>
                                                            </div>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="right">
                                                            {item.label}
                                                        </TooltipContent>
                                                    </Tooltip>
                                                ) : (
                                                    <InternalLink
                                                        to={item.route}
                                                        onClick={(e) =>
                                                            handleMenuItemClick(
                                                                e,
                                                                item.route,
                                                                item.onClick
                                                            )
                                                        }
                                                        className={`flex items-center h-10 p-2 rounded-lg group ${
                                                            item.restricted
                                                                ? "text-gray-400"
                                                                : isActiveRoute(
                                                                    item.route
                                                                )
                                                                ? "bg-blue-50 text-blue-600 font-medium"
                                                                : "text-gray-900 hover:bg-gray-100"
                                                        } ${
                                                            item.className || ""
                                                        }`}
                                                    >
                                                        <div className="relative min-w-[18px] flex items-center justify-center">
                                                            {item.icon && (
                                                                <Icon
                                                                    name={
                                                                        item.icon
                                                                    }
                                                                    size={18}
                                                                    className={
                                                                        item.restricted
                                                                            ? "text-gray-400"
                                                                            : isActiveRoute(
                                                                                item.route
                                                                            )
                                                                            ? "text-blue-600"
                                                                            : ""
                                                                    }
                                                                />
                                                            )}
                                                            {item.alertBadge === "integrationAlerts" && (
                                                                <IntegrationAlertBadge />
                                                            )}
                                                        </div>
                                                        <span className="ml-3 overflow-hidden whitespace-nowrap flex-1">
                                                            {item.label}
                                                        </span>
                                                        {item.restricted && (
                                                            <span className="text-[10px] text-purple-600 font-medium bg-purple-50 px-1.5 py-0.5 rounded whitespace-nowrap">
                                                                Innovator+
                                                            </span>
                                                        )}
                                                        {item.className?.includes("onboarding-glow-btn") && (
                                                            <div className="onboarding-snake" />
                                                        )}
                                                    </InternalLink>
                                                )}
                                            </li>
                                        ))}
                                </React.Fragment>
                            ))}
                        </ul>
                    </div>

                    {/* Interface Style Toggle */}
                    {/*isOpen ? (
                        <InterfaceStyleToggle
                            isOpen={isOpen}
                            interfaceStyle={interfaceStyle}
                            onToggle={toggleInterfaceStyle}
                        />
                    ) : (
                        <CollapsedInterfaceStyleToggle
                            interfaceStyle={interfaceStyle}
                            onToggle={toggleInterfaceStyle}
                        />
                    ) */}

                    {/* User Section at the bottom */}
                    <UserSection
                        isOpen={isOpen}
                        user={user}
                        hideProfile={hideProfile}
                    />
                </div>
            </aside>
        </>
    );
}

export default Sidebar;
